import { Injectable, computed, inject, signal } from '@angular/core'
import { NgxSpinnerService } from 'ngx-spinner'
import { ISiteConfiguration, IUser } from '@appShared/interfaces/[Interface-based]'
import { SubscriptionStatus_ } from '@appShared/services/lookup/[CodeGen]/subscription-status.domain'

export enum LoadingStatus {
   loading = 'loading',
   success = 'success',
   error = 'error'
}

//https://github.com/joshuamorony/signals-rxjs-demo/tree/main
export interface AppState {
   siteConfig: ISiteConfiguration | undefined
   currentUser: IUser | undefined,
   status: LoadingStatus
   error: string | null
}

/**
 * This class is solely to house global signals that all
 * components/services will have access to without
 * causing a circular reference
 * !! DO NOT add any logic/api/etc to this site !!
 * - only signals and update methods for those signals
 * Also, this class doesn't HAVE to be referenced
 * directly - it is referenced in CommonService which just
 * about every component/service references
 * (e.g., private _appSignals = this._commonService.appSignals )
 *
 */
@Injectable({ providedIn: 'root' })
export class AppSignals {

   /********************** App State **********************/
   private _appState = signal<AppState>({
      siteConfig: undefined,
      currentUser: undefined,
      status: LoadingStatus.loading,
      error: null
   })
   appState = computed(() => this._appState())
   siteConfig = computed(() => this.appState().siteConfig)
   setSiteConfig(siteConfig?: ISiteConfiguration): void {
      if (this._appState().siteConfig === siteConfig) return

      this._appState.update((state) => ({
         ...state,
         siteConfig,
         status: LoadingStatus.success
      }))
   }
   error = computed(() => this.appState().error)
   setError(error?: string) {
      this._appState.update((state) => ({
         ...state,
         status: LoadingStatus.error,
         error
      }))
   }
   currentUser = computed(() => this.appState().currentUser)
   setCurrentUser(currentUser?: IUser): void {
      if (this._appState().currentUser === currentUser) return

      this._appState.update((state) => ({
         ...state,
         currentUser,
         status: LoadingStatus.success
      }))
   }
   userSubscriptions = computed(() => this.currentUser()?.profile?.account?.subscriptions)
   userValidSubscription = computed(() => {
      const subscriptions = this.userSubscriptions()

      return subscriptions?.find(sub => [
         SubscriptionStatus_.Active,
         SubscriptionStatus_.Paused,
         SubscriptionStatus_.OnHold,
         SubscriptionStatus_.Cancelled,
         SubscriptionStatus_.Ended].includes(sub.statusCode))
   })
   newSubscription = computed(() => {
      return this.userSubscriptions()?.find(sub => sub.statusCode === SubscriptionStatus_.New)
   })
   userCanRegister = computed(() => {
      const currentUser = this.currentUser()
      if (!currentUser || currentUser.isAdmin || currentUser.isRep || currentUser.isPromo) {
         return false
      }

      const subscriptions = this.userSubscriptions()

      return !subscriptions?.find(sub => [
         SubscriptionStatus_.Active,
         SubscriptionStatus_.Paused,
         SubscriptionStatus_.OnHold].includes(sub.statusCode))
   })

   /*******************************************************/

   /********************** Loading ************************/
   private _spinnerService = inject(NgxSpinnerService)
   status = computed(() => this._appState().status)
   setStatus(status?: LoadingStatus): void {
      if (this._appState().status === status) return

      if (status === LoadingStatus.loading) {
         this._spinnerService.show()
      } else {
         this._spinnerService.hide()
      }
      this._appState.update((state) => ({
         ...state,
         status
      }))
   }
   /*******************************************************/

   /********************* MSAL/B2C ************************/
   private _msalIsLoggedIn = signal<boolean>(false)
   msalIsLoggedIn = computed(() => this._msalIsLoggedIn())
   setMsalIsLoggedIn(isLoggedIn?: boolean): void {
      this._msalIsLoggedIn.set(isLoggedIn || false)
   }
   /*******************************************************/

   /****************** Hybrid Interop *********************/
   hybridIsLoggingOut = signal<boolean>(false)
   /*******************************************************/
}
